import { Box, Grid, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
	DefaultValues,
	FormProvider,
	SubmitHandler,
	useForm,
} from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import { FormType } from '@/api/enum/formType.enum';
import {
	Dispatch,
	forwardRef,
	SetStateAction,
	useEffect,
	useImperativeHandle,
} from 'react';
import AppDatePicker from '@/common/components/form-inputs/AppDatePicker';
import dayjs from 'dayjs';
import { API_DATE_FORMAT, notFutureDate } from '@/utils/dateHelper';
import AppTextField from '@/common/components/form-inputs/AppTextField';
import SingleCheckbox from '@/common/components/form-inputs/SingleCheckbox';

interface Props {
	type: FormType;
	details?: any;
	setType: Dispatch<SetStateAction<FormType>>;
}

const EditReferrerInformationForm = forwardRef(
	({ type, setType, details }: Props, ref) => {
		const { t } = useTranslation();
		const defaultValues: DefaultValues<any> = {};

		useEffect(() => {
			if (details) methods.reset(details);
		}, [details]);

		const formSchema = Yup.object().shape({
			discussionDate: Yup.string()
				.nullable()
				.test(notFutureDate(t('errorMessage.dateCannotBeFuture'))),
		});
		const methods = useForm<any>({
			defaultValues,
			resolver: yupResolver(formSchema),
			mode: 'onChange',
			shouldFocusError: true,
		});

		useImperativeHandle(ref, () => ({
			getFormData: () => methods.getValues(),
			validateForm: () => {
				methods.trigger();
				methods.formState.isValid;
				return methods.trigger().then(() => methods.formState.isValid);
			},
		}));

		return (
			<>
				<FormProvider {...methods}>
					<Typography variant='h6' sx={{ fontWeight: 'bold' }} marginBottom={2}>
						{t('preAdmission.referrerInformation')}
					</Typography>
					<form onSubmit={methods.handleSubmit((e: any) => e.preventDefault())}>
						<Grid container spacing={3} marginTop={0} marginBottom={3}>
							<Grid size={6}>
								<AppTextField
									control={methods.control}
									name='nameOfReferrer'
									label={t('preAdmission.nameOfReferrer')}
								/>
							</Grid>
							<Grid size={6}>
								<AppTextField
									control={methods.control}
									name='iccmwWorkerName'
									label={t('preAdmission.nameOfICCMWWorker')}
								/>
							</Grid>
							<Grid size={6}>
								<AppDatePicker
									maximumDate={dayjs().startOf('day').format(API_DATE_FORMAT)}
									control={methods.control}
									name='discussionDate'
									label={t('preAdmission.discussionDate')}
								/>
							</Grid>
							<Grid size={6}></Grid>
							<Grid size={12}>
								<Typography variant='body1'>
									{t('preAdmission.wouldLikeToRefer')}
								</Typography>
								<Box display={'flex'}>
									<Grid size={6}>
										<Box display={'flex'}>
											<SingleCheckbox
												control={methods.control}
												name='referralIsHis'
												label={t('preAdmission.his')}
												handleChange={(checked: boolean) => {
													if (checked) methods.setValue('referralIsHer', false);
												}}
											/>
											<SingleCheckbox
												control={methods.control}
												name='referralIsHer'
												label={t('preAdmission.her')}
												handleChange={(checked: boolean) => {
													if (checked) methods.setValue('referralIsHis', false);
												}}
											/>
										</Box>
									</Grid>
									<Grid size={6}>
										<Box display={'flex'}>
											<SingleCheckbox
												control={methods.control}
												name='mentalHealth'
												label={t('preAdmission.mentalHealth')}
												handleChange={(checked: boolean) => {
													if (checked)
														methods.setValue('suspectedMentalHealth', false);
												}}
											/>
											<SingleCheckbox
												control={methods.control}
												name='suspectedMentalHealth'
												label={t('preAdmission.suspectedMentalHealthProblem_')}
												handleChange={(checked: boolean) => {
													if (checked) methods.setValue('mentalHealth', false);
												}}
											/>
										</Box>
									</Grid>
								</Box>
							</Grid>
						</Grid>
					</form>
				</FormProvider>
			</>
		);
	}
);

EditReferrerInformationForm.displayName = 'EditReferrerInformationForm';

export default EditReferrerInformationForm;
